import api from "./api";
import { enqueueAndWait } from "./uploadManager";
import { BACKEND_URL } from "../lib/api";

const KIND_BY_MIME = (mime) => {
  if (!mime) return "file";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "file";
};

/**
 * Wave 26.8 — `uploadFile()` now routes through the singleton upload
 * manager (`lib/uploadManager.js`). This means every existing caller
 * (JC photos, ticket attachments, customer gallery, lead media, bug
 * report attachments, customer-portal uploads, …) becomes
 * background-resilient *for free* — even if the calling component
 * unmounts mid-upload (very common when a tech is firing off 15
 * photos and the keyboard shifts the page) the upload continues and
 * the manager records the result.
 *
 * Callers that need rich progress UI (per-task spinner, retry button,
 * Uploading-N-of-M pill) should switch to `enqueue()` + `useUploads()`
 * directly.
 *
 * @param {File} file
 * @param {object} [opts] — optional metadata for the upload dock
 * @param {string} [opts.target_kind]
 * @param {string} [opts.target_id]
 * @param {string} [opts.label]
 */
export async function uploadFile(file, opts = {}) {
  return enqueueAndWait({
    file,
    target_kind: opts.target_kind || null,
    target_id: opts.target_id || null,
    label: opts.label || null,
    source: opts.source || null,
  });
}

/** [Iter 327 — 2026-02-24] Single-source-of-truth image URL builder.
 *
 * Accepts ANY of:
 *   • Bare upload id           →  "abc123-def456"
 *   • App path                 →  "/api/uploads/abc123-def456"
 *   • Full URL (fresh or stale)→  "https://backend/api/uploads/abc123?auth=OLDTOKEN"
 *   • R2 presigned URL         →  "https://…r2.cloudflarestorage.com/…"
 *   • Data URI                 →  "data:image/png;base64,…"
 *   • Blob URI                 →  "blob:https://…"
 *
 * For anything that resolves to our `/api/uploads/{id}` endpoint we
 * **always** strip the id and re-emit the URL with the CURRENT
 * `hv_token` from localStorage. This kills the "stale JWT baked in
 * at write-time" bug class at a single choke-point — every consumer
 * (JC, Chat, Gallery, CRM, Portal, PDF anchors, share menu, before/
 * after, receipts, datasheets, project picking, lead media …) just
 * calls `uploadUrl(anything)` and gets a fresh, live URL.
 *
 * Non-upload URLs (R2 presigned, data:, blob:, third-party) are
 * returned unchanged.
 *
 * Returns "" (empty string) when the input is falsy — callers should
 * treat empty as "no image" and NOT set `<img src="">` (which fires
 * a request to the current page). Most callers already guard on this.
 */
export function uploadUrl(input) {
  if (!input) return "";
  const base = BACKEND_URL;
  const token = localStorage.getItem("hv_token") || "";
  const stamp = (id) =>
    `${base}/api/uploads/${encodeURIComponent(id)}?auth=${encodeURIComponent(token)}`;

  if (typeof input !== "string") return "";
  const s = input.trim();
  if (!s) return "";

  // Passthrough: non-upload URIs we don't own.
  if (s.startsWith("data:") || s.startsWith("blob:")) return s;

  // Passthrough: R2 presigned URLs (or any non-uploads http URL).
  const httpish = s.startsWith("http://") || s.startsWith("https://");
  if (httpish && !s.includes("/api/uploads/")) return s;

  // Extract the bare upload id from any recognised shape.
  //   /api/uploads/{id}[?…]
  //   {id}
  const uploadsMatch = s.match(/\/api\/uploads\/([^/?#]+)/);
  const id = uploadsMatch ? uploadsMatch[1] : s.split("?")[0].split("#")[0];
  if (!id) return "";
  return stamp(id);
}

export async function deleteUpload(id) {
  await api.delete(`/uploads/${id}`);
}

export { KIND_BY_MIME };
